import QueryString from 'qs';
import { CitationClass } from '../../types';
import { DOCUMENT_TYPE_METADATAS, DocumentType } from '../../constants';
import { parseKnowledgeBaseCitation } from './knowledgeBaseCitation';

/**
 * @param reference reference string from backend
 * @returns citation object
 */
const isDocumentType = (value: unknown): value is DocumentType => {
  return typeof value === 'string' && value in DOCUMENT_TYPE_METADATAS;
};

export const getCitationClass = (reference?: string | null): CitationClass => {
  if (typeof reference !== 'string' || reference.trim() === '') {
    return {};
  }

  const knowledgeBase = parseKnowledgeBaseCitation(reference);
  if (knowledgeBase) {
    return knowledgeBase;
  }

  // medical result
  if (reference.includes('|')) {
    const [system, key, resourceId] = reference.split('|');
    return {
      system,
      key,
      resourceId,
      citationType: 'MedicalResult',
      title: undefined, // later we might add reference titles (name of test, based on the system/key or some provided display name)
    };
  }
  if (reference.startsWith('DocumentReference')) {
    // document reference
    const [path, query] = reference.split('?');
    const { documentType } = QueryString.parse(query);
    const [, resourceId] = path.split('/');
    const documentTypeDescription = isDocumentType(documentType)
      ? DOCUMENT_TYPE_METADATAS[documentType].name
      : undefined;
    return {
      citationType: 'DocumentReference',
      resourceId,
      query,
      title: documentTypeDescription || 'Document Reference',
    };
  }
  if (reference.startsWith('PubMedPaper')) {
    // pubmed paper
    const [, pmid] = reference.split('/');
    return {
      pmid,
      citationType: 'PubMedPaper',
      title: 'Medical Research Paper',
    };
  }
  if (reference.startsWith('QuestionnaireResponse')) {
    const [path, query] = reference.split('?');
    const [, resourceId] = path.split('/');
    if (!resourceId) {
      return {};
    }
    const { primarySurveyId } = QueryString.parse(query);
    return {
      citationType: 'QuestionnaireResponse',
      resourceId,
      primarySurveyId:
        typeof primarySurveyId === 'string' ? primarySurveyId : undefined,
      title: 'Questionnaire Response',
    };
  }
  return {};
};
